import i18nFactory from '@yandex-int/i18n';
import { union } from 'lodash';

import { LogHelpers } from '../../../service/LogHelpers/LogHelpers';
import { SimpleMultipliersHelpers } from '../../../service/SimpleMultipliersHelpers/SimpleMultipliersHelpers';
import {
    Age,
    DemographyMultiplierFragment,
    Gender,
    OutputCategory,
    RetargetingFragment
} from '../../../typings/graphql-log';
import {
    ChangedOptions,
    DemographyMultiplierValue,
    MultipliersGroups,
    MultiplierValue,
    RetargetingValue,
    SimpleMultiplierGroups,
    SourceMultiplierValue
} from '../../../typings/log';

import * as keyset from './multipliers.i18n';

const i18n = i18nFactory(keyset);

export const mapAgeToText = (age: Age): string => {
    switch (age) {
        case Age.age_0_17:
            return i18n('age_0_17');
        case Age.age_18_24:
            return i18n('age_18_24');
        case Age.age_25_34:
            return i18n('age_25_34');
        case Age.age_35_44:
            return i18n('age_35_44');
        case Age.age_45_54:
            return i18n('age_45_54');
        case Age.age_45_inf:
            return i18n('age_45_inf');
        case Age.age_55_inf:
            return i18n('age_55_inf');
        case Age.any:
        default:
            return i18n('any');
    }
};

export const mapGenderToText = (gender: Gender): string => {
    switch (gender) {
        case Gender.female:
            return i18n('gender_female');
        case Gender.male:
            return i18n('gender_male');
        case Gender.any:
        default:
            return i18n('gender_any');
    }
};

const noCorrectionValue = LogHelpers.noCorrectionValue;

export function prepareRetargetingGroups(
    before?: Array<RetargetingFragment | null>,
    after?: Array<RetargetingFragment | null>
): MultipliersGroups {
    if (!before || !after) {
        throw Error('Некорректное значение корректировок на ретаргетинг: нет данных');
    }

    return prepareMultipliers(
        before.filter(val => val !== null).map(({ __typename, ...rest }: RetargetingFragment) => rest) as Array<
            RetargetingFragment
        >,
        after.filter(val => val !== null).map(({ __typename, ...rest }: RetargetingFragment) => rest) as Array<
            RetargetingFragment
        >,
        calcRetargetingLineLength,
        getRetargetingMultiplierId
    );
}

export function prepareDemographyGroups(
    before?: Array<DemographyMultiplierFragment | null>,
    after?: Array<DemographyMultiplierFragment | null>
): MultipliersGroups {
    if (!before || !after) {
        throw Error('Некорректное значение корректировок на ретаргетинг: нет данных');
    }

    return prepareMultipliers(
        before
            .filter(val => val !== null)
            .map(({ __typename, ...rest }: DemographyMultiplierFragment) => rest) as Array<
            DemographyMultiplierFragment
        >,
        after.filter(val => val !== null).map(({ __typename, ...rest }: DemographyMultiplierFragment) => rest) as Array<
            DemographyMultiplierFragment
        >,
        calcDemographyLineLength,
        getDemographyMultiplierId
    );
}

export function prepareSimpleMultipliersGroups(
    category: OutputCategory,
    before?: number,
    after?: number
): SimpleMultiplierGroups {
    if (before === undefined || after === undefined) {
        throw Error('Некорректное значение для корректировок таргетинга: нет данных');
    }

    const type =
        before === LogHelpers.noCorrectionValue
            ? ChangedOptions.added
            : after === LogHelpers.noCorrectionValue
            ? ChangedOptions.removed
            : ChangedOptions.modified;

    let symbolsLength = 0;
    switch (type) {
        case ChangedOptions.added:
            symbolsLength = after.toString().length + 2; // %+пробел
            break;
        case ChangedOptions.removed:
            symbolsLength = before.toString().length + 2; // %+пробел
            break;
        case ChangedOptions.modified:
            symbolsLength = after.toString().length + before.toString().length + 2; // %+пробел 2 раза
            break;
        default:
            break;
    }
    const maxLineLength = symbolsLength + SimpleMultipliersHelpers.getSimpleMultiplier(category).length;

    return {
        compareValue: before,
        value: after,
        type: type,
        alwaysNeedExpand: false,
        maxLineLength: maxLineLength,
        needExpand: LogHelpers.calcNotAlwaysNeedExpand(maxLineLength)
    };
}

function reduceMultiplier(
    res: Map<string, MultiplierValue>,
    value: SourceMultiplierValue,
    getId: (value: SourceMultiplierValue) => string
): Map<string, MultiplierValue> {
    const id = getId(value),
        newValue = {
            ...value,
            id,
            compareMultiplier: 0,
            type: ChangedOptions.notModified
        };
    res.set(id, newValue);
    return res;
}

function mapMultiplierValue(
    id: string,
    calcMultipliersLineLength: (value: MultiplierValue) => number,
    beforeValue?: MultiplierValue,
    afterValue?: MultiplierValue
): { value: MultiplierValue; lineLength: number } {
    const beforeMultiplier = beforeValue ? beforeValue.multiplier : 0,
        afterMultiplier = afterValue ? afterValue.multiplier : 0,
        sourceValue = beforeValue ? beforeValue : afterValue,
        newValue: MultiplierValue = {
            ...sourceValue,
            id: id,
            type: ChangedOptions.modified,
            multiplier: afterMultiplier,
            compareMultiplier: beforeMultiplier
        };

    if (beforeValue === undefined && afterValue !== undefined) {
        newValue.type = ChangedOptions.added;
    } else if (beforeValue !== undefined && afterValue === undefined) {
        newValue.type = ChangedOptions.removed;
    } else {
        if (beforeMultiplier === afterMultiplier) {
            newValue.type = ChangedOptions.notModified;
        } else {
            newValue.type = ChangedOptions.modified;
        }
    }

    const lineLength = calcMultipliersLineLength(newValue);

    return {
        value: newValue,
        lineLength: lineLength
    };
}

function getRetargetingMultiplierId(value: RetargetingValue): string {
    return value.retargetingConditionId.toString();
}

function getDemographyMultiplierId(value: DemographyMultiplierValue): string {
    return value.age + value.gender;
}

function calcRetargetingLineLength(value: RetargetingValue): number {
    const specialSymbolsCount = 3, // '+% '
        nameIsUppercase = value.retargetingConditionName.toUpperCase() === value.retargetingConditionName,
        // дополнительная длина из-за апперкейсных символов, так как шрифт не моноширинный
        addedSymbolsCount = nameIsUppercase ? Math.ceil(value.retargetingConditionName.trim().length / 4) : 0,
        conditionStr = i18n('on-condition', {
            condition: value.retargetingConditionName.trim()
        }),
        conditionLength = conditionStr.length,
        hasTwoCorrections = value.type === ChangedOptions.modified,
        isRemoved = value.type === ChangedOptions.removed,
        correctionLength =
            (isRemoved ? 0 : Math.abs(value.multiplier - noCorrectionValue).toString().length) +
            (hasTwoCorrections || isRemoved
                ? Math.abs((value.compareMultiplier || noCorrectionValue) - noCorrectionValue).toString().length
                : 0);

    return (
        conditionLength +
        correctionLength +
        (hasTwoCorrections ? specialSymbolsCount * 2 : specialSymbolsCount) +
        addedSymbolsCount
    );
}

function calcDemographyLineLength(value: DemographyMultiplierValue): number {
    const specialSymbolsCount = 3, // '+% '
        condition = i18n('gender-and-age', {
            gender: mapGenderToText(value.gender),
            age: mapAgeToText(value.age)
        }),
        conditionStr = i18n('on-condition', {
            condition: condition
        }),
        conditionLength = conditionStr.length,
        hasTwoCorrections = value.type === ChangedOptions.modified,
        isRemoved = value.type === ChangedOptions.removed,
        correctionLength =
            (isRemoved ? 0 : Math.abs(value.multiplier - noCorrectionValue).toString().length) +
            (hasTwoCorrections || isRemoved
                ? Math.abs((value.compareMultiplier || noCorrectionValue) - noCorrectionValue).toString().length
                : 0);

    return conditionLength + (hasTwoCorrections ? specialSymbolsCount * 2 : specialSymbolsCount) + correctionLength;
}

function prepareMultipliers(
    before: Array<SourceMultiplierValue>,
    after: Array<SourceMultiplierValue>,
    countMultiplierLineLength: (value: MultiplierValue) => number,
    getId: (value: MultiplierValue) => string
): MultipliersGroups {
    let maxLineLength = 0;

    const beforeMap = before.reduce((res: Map<string, MultiplierValue>, value: SourceMultiplierValue) => {
            return reduceMultiplier(res, value, getId);
        }, new Map<string, MultiplierValue>()),
        afterMap = after.reduce((res: Map<string, MultiplierValue>, value: SourceMultiplierValue) => {
            return reduceMultiplier(res, value, getId);
        }, new Map<string, MultiplierValue>()),
        beforeIds = Array.from(beforeMap.keys()),
        afterIds = Array.from(afterMap.keys()),
        allIds = union(beforeIds, afterIds),
        allMapped = allIds.map((id: string) => {
            const beforeValue = beforeMap.get(id),
                afterValue = afterMap.get(id),
                mapped = mapMultiplierValue(id, countMultiplierLineLength, beforeValue, afterValue);
            maxLineLength = Math.max(maxLineLength, mapped.lineLength);
            return mapped.value;
        });

    return {
        value: allMapped,
        alwaysNeedExpand: allMapped.length > 4,
        maxLineLength: maxLineLength,
        needExpand: LogHelpers.calcNeedExpand(allMapped.length > 4, maxLineLength)
    };
}
